import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useSearchParams } from 'react-router-dom';
import { OrganisationContext, UserContext } from '../../../context';
import { OrganisationTagType, storyCardApi } from '../../../services';
import {
  cookieOptions,
  Filter,
  FilterGroup,
  FilterValue,
  optArrItem,
  SEARCH_ADMIN_COOKIE_KEY,
  useAppDispatch,
  useAuthorsFilter,
  useCardCategoryFilter,
  useCardStatusFilter,
  useCardStoryFilter,
  useCardTypeFilter,
  useDateRangeFilter,
  useFilter,
  useSearchByTags,
  useSourcesFilter,
  useTags,
  useTagsFilter,
} from '../../../shared';
import { getSearchFeed } from '../../../slices';

import classes from './SearchFeedAdminRightSide.module.scss';

export const SearchFeedAdminRightSide: FunctionComponent = () => {
  const dispatch = useAppDispatch();

  const [fetchAuthors] = storyCardApi.endpoints.storyCardAuthorsFiltersByChannelId.useLazyQuery();

  const [fetchSources] = storyCardApi.endpoints.storyCardSourcesFiltersByChannelId.useLazyQuery();

  const [searchParams] = useSearchParams();

  const { channelId } = useContext(UserContext).userInfo.userData;

  const { getGroupValue, getDateRangeValue } = useFilter();

  const { hasInternalTags, hasExternalTags, hasExternalStatusTags } = useTags();

  const { tagsSearchParams } = useSearchByTags();

  const hasCategories = Boolean(useContext(OrganisationContext).organisation.categories.length);

  const [cookies, setCookie] = useCookies([SEARCH_ADMIN_COOKIE_KEY]);

  const [filter, setFilter] = useState<FilterValue[]>(
    cookies[SEARCH_ADMIN_COOKIE_KEY]?.filter ?? []
  );

  const { group: dateRangeGroup } = useDateRangeFilter({ filter });

  const { group: authorsGroup } = useAuthorsFilter({
    filter,
    fetchItemsQuery: fetchAuthors,
    fetchItemsQueryArgs: { channelId },
  });

  const { group: sourcesGroup } = useSourcesFilter({
    filter,
    fetchItemsQuery: fetchSources,
    fetchItemsQueryArgs: { channelId },
  });

  const { group: categoryGroup } = useCardCategoryFilter({ filter });

  const { group: internalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.INTERNAL],
  });

  const { group: externalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.EXTERNAL],
  });

  const { group: externalStatusTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.EXTERNAL_STATUS],
  });

  const { group: cardTypeGroup } = useCardTypeFilter();

  const { group: cardStatusGroup } = useCardStatusFilter();

  const { group: cardStoryGroup } = useCardStoryFilter({ filter });

  const query = (searchParams.get('qs') ?? '').replace(/\\|"|'/g, '');

  const groups: FilterGroup[] = useMemo(
    () => [
      ...optArrItem(hasInternalTags && !tagsSearchParams, internalTagsGroup),
      ...optArrItem(hasExternalTags && !tagsSearchParams, externalTagsGroup),
      ...optArrItem(hasExternalStatusTags && !tagsSearchParams, externalStatusTagsGroup),
      ...optArrItem(hasCategories, categoryGroup),
      authorsGroup,
      sourcesGroup,
      cardStoryGroup,
      dateRangeGroup,
      cardTypeGroup,
      cardStatusGroup,
    ],
    [
      authorsGroup,
      cardStatusGroup,
      cardStoryGroup,
      cardTypeGroup,
      categoryGroup,
      dateRangeGroup,
      externalStatusTagsGroup,
      externalTagsGroup,
      hasCategories,
      hasExternalStatusTags,
      hasExternalTags,
      hasInternalTags,
      internalTagsGroup,
      sourcesGroup,
      tagsSearchParams,
    ]
  );

  const filterChange = useCallback(
    (filter: FilterValue[]) => {
      const commonIdProps = { filter, multiCheck: true, propToMap: 'id' };

      const authorId = getGroupValue({ ...commonIdProps, groupId: authorsGroup.id });

      const sourceName = getGroupValue({ ...commonIdProps, groupId: sourcesGroup.id });

      const categoryId = getGroupValue({ ...commonIdProps, groupId: categoryGroup.id });

      const internalTagsId = getGroupValue({ ...commonIdProps, groupId: internalTagsGroup.id });

      const externalTagsId = getGroupValue({ ...commonIdProps, groupId: externalTagsGroup.id });

      const externalStatusTagsId = getGroupValue({
        ...commonIdProps,
        groupId: externalStatusTagsGroup.id,
      });

      const storyId = getGroupValue({ ...commonIdProps, groupId: cardStoryGroup.id });

      const storyCardType = getGroupValue({ filter, groupId: cardTypeGroup.id, multiCheck: true });

      const status = getGroupValue({ filter, groupId: cardStatusGroup.id, multiCheck: true });

      const { dateRange, from, to } = getDateRangeValue({ filter, groupId: dateRangeGroup.id });

      const common = {
        ...(authorId?.length && { authorId }),
        ...(sourceName?.length && { sourceName }),
        ...(categoryId?.length && { categoryId }),
        ...((internalTagsId?.length || externalTagsId?.length || externalStatusTagsId?.length) && {
          organisationTags: { internalTagsId, externalTagsId, externalStatusTagsId },
        }),
        ...(storyId?.length && { storyId }),
        ...(storyCardType?.length && { storyCardType }),
        ...(status?.length && { status }),
      };

      const requestFilterToSave = { ...common, dateRange };

      const requestFilter = { ...common, ...(from && { from }), ...(to && { to }) };

      setCookie(
        SEARCH_ADMIN_COOKIE_KEY,
        { requestFilter: requestFilterToSave, filter },
        cookieOptions()
      );

      dispatch(getSearchFeed({ channelId, filters: { ...requestFilter, query } }));

      setFilter(filter);
    },
    [
      authorsGroup.id,
      cardStatusGroup.id,
      cardStoryGroup.id,
      cardTypeGroup.id,
      categoryGroup.id,
      channelId,
      dateRangeGroup.id,
      dispatch,
      externalStatusTagsGroup.id,
      externalTagsGroup.id,
      getDateRangeValue,
      getGroupValue,
      internalTagsGroup.id,
      query,
      setCookie,
      sourcesGroup.id,
    ]
  );

  return (
    <div className={classes['search-feed-admin-right-side']}>
      <Filter filter={filter} groups={groups} onChange={filterChange} resetPageScrollOnChange />
    </div>
  );
};
